import { Icon } from '@assembly-js/design-system';
import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  ADD_APP_PAGE,
  APP_SETUP_PAGE,
  AUTOMATIONS_PAGE,
  CLIENT_DETAILS_PAGE,
  CONTRACT_DETAILS_PAGE,
  CONTRACTS_PAGE,
  FORMS_DETAIL_PAGE_V2,
  FORMS_PAGE,
  INVOICE_DETAILS_PAGE_PATH,
  PAYMENTS_SERVICE_DETAILS_PAGE,
} from 'src/constants';
import { RouteContext } from 'src/context';
import MenuLogo from 'src/features/drawer/MenuLogo';
import history from 'src/history';
import { useContentfulApps } from 'src/hooks/useContentfulApps';
import { useAppDispatch } from 'src/hooks/useStore';
import { SETTINGS_AUTOMATIONS_PATH } from 'src/legacy/components/automations/useAutomationsBasePath';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
  },
  menuIconWrapper: {
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1.5),
    },
  },
}));

/**
 * @description This is deprecated. Please use the new TableToolbar component in src/hooks/TableToolbar.tsx
 * @deprecated use the new component in src/features/layout/TableToolbar.tsx
 */
export const TableToolbar: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const { pathname } = React.useContext(RouteContext);
  const id = pathname.split('/').pop();
  const { classes } = useStyles();

  const { allApps, getCoreApp } = useContentfulApps({
    includeInstalledApps: false,
  });

  // get all app details pathnames
  // we will use this to show back button on these pages
  const appDetailsPathnames = useMemo(() => {
    if (!allApps) {
      return [];
    }

    // on app details pathname is generated based on the app type
    // for core modules we  use 'copilotAppId'
    // for non-core modules we  use 'slug'
    return allApps.map(
      (a) =>
        `${APP_SETUP_PAGE.path}/${
          getCoreApp(a.copilotAppId) ? a.copilotAppId : a.slug
        }`,
    );
  }, [allApps]);

  const pathsToShowBackButton = [
    FORMS_DETAIL_PAGE_V2.path,
    `${FORMS_PAGE.path}/response`,
    `${FORMS_PAGE.path}/submit`,
    CONTRACT_DETAILS_PAGE.path,
    CLIENT_DETAILS_PAGE.path,
    ADD_APP_PAGE.path,
    `${CONTRACTS_PAGE.path}/submission`,
    `${PAYMENTS_SERVICE_DETAILS_PAGE.path}/${id}`,
    INVOICE_DETAILS_PAGE_PATH,
    `${AUTOMATIONS_PAGE.path}/details`,
    `${AUTOMATIONS_PAGE.path}/runs/details`,
    `${SETTINGS_AUTOMATIONS_PATH}/details`,
    `${SETTINGS_AUTOMATIONS_PATH}/runs/details`,
    ...appDetailsPathnames,
  ];

  const showBackNavigationButton = pathsToShowBackButton.some((path) =>
    pathname.startsWith(path),
  );

  const handleMenuButtonClick = React.useCallback(() => {
    // navigate back to last page
    // when back button is visible
    if (showBackNavigationButton) {
      history.goBack();
      return;
    }

    // by default on menu click open the primary sidebar
    dispatch(togglePrimarySidebarMobile({ isOpen: true }));
  }, [showBackNavigationButton]);

  return (
    <div className={classes.root}>
      <div className={classes.menuIconWrapper}>
        <MenuLogo
          menuClickCallback={handleMenuButtonClick}
          Icon={
            showBackNavigationButton ? (
              <Icon icon="ChevronLeft" />
            ) : (
              <Icon icon="Menu" />
            )
          }
        />
      </div>
      {children}
    </div>
  );
};
